import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Eye, EyeOff, Check } from 'lucide-react';
import SEO from '../components/SEO';
import LocaleLink from '../components/LocaleLink';

const LOGO_URL = 'https://quidory.com/media/quidory-logo.png';

const SignupPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '' });

  const lc = {
    headline: t('auth.signup.headline'),
    sub: t('auth.signup.subheadline'),
    nameLabel: t('auth.signup.name'),
    namePh: t('auth.signup.namePlaceholder'),
    emailLabel: t('auth.signup.email'),
    emailPh: t('auth.signup.emailPlaceholder'),
    pwLabel: t('auth.signup.password'),
    pwPh: t('auth.signup.passwordPlaceholder'),
    submit: t('auth.signup.submit'),
    hasAccount: t('auth.signup.hasAccount'),
    login: t('auth.signup.login'),
    terms: t('auth.signup.terms'),
    termsLink: t('auth.signup.termsLink'),
    and: t('auth.signup.and'),
    privacyLink: t('auth.signup.privacyLink'),
  };

  const perks = language === 'ar'
    ? ['وصول فوري لـ QuidPulse', 'لا يلزم بطاقة ائتمان', 'إلغاء في أي وقت']
    : ['Instant QuidPulse access', 'No credit card required', 'Cancel anytime'];

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        titleEn="Create Account | Quidory"
        titleAr="إنشاء حساب | كويدوري"
        descEn="Create your Quidory account and start using AI-powered multilingual intelligence and publishing tools."
        descAr="أنشئ حساب كويدوري وابدأ في استخدام أدوات الذكاء الاصطناعي والنشر الآلي متعددة اللغات."
        path="/signup"
      />
      <div className="p-6">
        <LocaleLink to="/" className="flex items-center gap-2 w-fit">
          <img src={LOGO_URL} alt="Quidory" className="h-7 w-7 object-contain" />
          <span className="font-semibold text-foreground text-sm">
            {language === 'ar' ? 'كويدوري' : 'Quidory'}
          </span>
        </LocaleLink>
      </div>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-light tracking-tight text-foreground mb-2">{lc.headline}</h1>
            <p className="text-muted-foreground text-sm">{lc.sub}</p>
          </div>

          {/* Perks */}
          <div className="flex justify-center gap-4 mb-6 flex-wrap">
            {perks.map((perk, i) => (
              <span key={i} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Check className="h-3 w-3 text-gold" />
                {perk}
              </span>
            ))}
          </div>

          <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
            <form
              data-testid="signup-form"
              onSubmit={e => { e.preventDefault(); navigate('/dashboard'); }}
              className="space-y-5"
            >
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{lc.nameLabel}</label>
                <input
                  data-testid="signup-name"
                  type="text"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder={lc.namePh}
                  required
                  className="w-full px-4 py-2.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground/60 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{lc.emailLabel}</label>
                <input
                  data-testid="signup-email"
                  type="email"
                  value={form.email}
                  onChange={e => setForm({ ...form, email: e.target.value })}
                  placeholder={lc.emailPh}
                  required
                  dir="ltr"
                  className="w-full px-4 py-2.5 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground/60 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">{lc.pwLabel}</label>
                <div className="relative">
                  <input
                    data-testid="signup-password"
                    type={showPw ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    placeholder={lc.pwPh}
                    required
                    dir="ltr"
                    minLength={8}
                    className="w-full px-4 py-2.5 pe-11 rounded-lg border border-border bg-background text-foreground placeholder:text-muted-foreground/60 text-sm focus:outline-none focus:ring-2 focus:ring-gold/30 focus:border-gold/40 transition-colors"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPw(!showPw)}
                    className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <button
                data-testid="signup-submit"
                type="submit"
                className="w-full py-3 rounded-lg bg-foreground text-background font-medium text-sm hover:bg-foreground/90 transition-colors"
              >
                {lc.submit}
              </button>
            </form>

            <p className="mt-5 text-center text-xs text-muted-foreground leading-relaxed">
              {lc.terms}{' '}
              <LocaleLink to="/terms" className="text-foreground underline underline-offset-2">{lc.termsLink}</LocaleLink>
              {' '}{lc.and}{' '}
              <LocaleLink to="/privacy" className="text-foreground underline underline-offset-2">{lc.privacyLink}</LocaleLink>
            </p>

            <p className="mt-4 text-center text-sm text-muted-foreground">
              {lc.hasAccount}{' '}
              <LocaleLink to="/login" data-testid="goto-login" className="text-foreground font-medium hover:text-gold transition-colors">
                {lc.login}
              </LocaleLink>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
